import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import countryCodes from "./countryCodes.json";
import { Button } from "./shadcn/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./shadcn/command";
import { FormControl, FormDescription } from "./shadcn/form";
import { Popover, PopoverContent, PopoverTrigger } from "./shadcn/popover";

interface CountryCode {
  countryCode: string;
  dialCode: string;
}

interface CountryCodeInputProps {
  description: string;
  value: CountryCode;
  onChange: (value: CountryCode) => void;
  readOnly?: boolean;
  testId?: string;
  disabled?: boolean;
}

export const CountryCodeInput = ({
  description,
  value,
  onChange,
  readOnly = false,
  testId,
  disabled = false,
}: CountryCodeInputProps) => {
  const selectedCountry = useMemo(() => {
    return countryCodes.find((code) =>
      value.dialCode.startsWith(code.dial_code),
    );
  }, [value.dialCode]);

  const [open, setOpen] = useState(false);

  return (
    <>
      <FormDescription className="sr-only">{description}</FormDescription>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild disabled={readOnly || disabled}>
          <FormControl>
            <Button
              variant="outline"
              role="combobox"
              className={cn(
                "w-[90px] h-full m-0 justify-between",
                !value && "text-muted-foreground",
                (readOnly || disabled) &&
                  "bg-gray-50 border-gray-200 text-gray-900 cursor-default",
              )}
              data-testid={testId}
              data-focus-target="true"
            >
              {selectedCountry ? selectedCountry.dial_code : "+971"}
              <ChevronsUpDown className="opacity-50" />
            </Button>
          </FormControl>
        </PopoverTrigger>
        <PopoverContent className="w-[250px] p-0 z-50 pointer-events-auto h-[250px] ml-5">
          <Command>
            <CommandInput
              placeholder="Search country code..."
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>No country code found.</CommandEmpty>
              <CommandGroup>
                {countryCodes.map((country) => (
                  <CommandItem
                    value={`${country.name} (${country.dial_code}) `}
                    key={country.code}
                    onSelect={() => {
                      onChange({
                        countryCode: country.code,
                        dialCode: country.dial_code,
                      });
                      setOpen(false);
                    }}
                  >
                    <span className="text-left">
                      {country.name} ({country.dial_code})
                    </span>
                    <Check
                      className={cn(
                        "ml-auto",
                        country.dial_code === value.dialCode
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
};
